import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Sparkles, EyeOff, Eye, AlertTriangle, ShieldCheck, Filter, HelpCircle } from 'lucide-react';

// Phase 3/4 UI — Persistent Results feed with Attention Reduction KPI +
// per-result trust/match/verification/conflict signals. Zita Doctrine:
// Find → Organize → Remember → Compare → Verify → Prioritize → Act.

const bucketLabel = {
  surface: 'In your feed',
  review:  'Needs review',
  hidden:  'Hidden',
};

const bucketBadge = (b) => ({
  surface: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  review:  'bg-amber-50 text-amber-800 border-amber-200',
  hidden:  'bg-slate-100 text-slate-500 border-slate-200',
}[b] || 'bg-slate-100 text-slate-500 border-slate-200');

const DECISIONS = [
  { key: 'relevant',     label: 'Relevant' },
  { key: 'not_relevant', label: 'Not relevant' },
  { key: 'hidden',       label: 'Hide' },
  { key: 'needs_review', label: 'Flag for review' },
  { key: 'contacted',    label: 'Contacted' },
  { key: 'purchased',    label: 'Purchased' },
  { key: 'cleared',      label: 'Clear decision' },
];

const ScoreBar = ({ label, value }) => {
  const pct = Math.round(((value || 0) * 100));
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={`score-${label.toLowerCase()}`}>
      <span className="text-slate-500 w-24 shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${pct}%`,
            background: pct > 66 ? 'var(--zita-orange,#F5661C)' : pct > 33 ? '#F59E0B' : '#94A3B8',
          }}
        />
      </div>
      <span className="tabular-nums w-9 text-right text-slate-700 font-medium">{pct}%</span>
    </div>
  );
};

const ResultCard = ({ r, onDecide, onExplain }) => {
  const conflicts = r.conflicting_evidence || [];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 flex flex-col gap-3"
         data-testid={`result-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${bucketBadge(r.attention_bucket)} uppercase tracking-wide font-semibold`}>
              {bucketLabel[r.attention_bucket] || r.attention_bucket}
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
              {r.source_name || r.source_key}
            </span>
            <span className="text-[10px] text-slate-500">
              seen {r.sighting_count || 1}×
            </span>
            {conflicts.length > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-semibold"
                data-testid="conflict-badge"
                title="Conflicting public evidence detected across sources"
              >
                <AlertTriangle size={12} />
                Conflict × {conflicts.length}
              </span>
            )}
          </div>
          <h3 className="mt-2 font-semibold text-slate-900 leading-snug line-clamp-2" data-testid="result-title">
            <a href={r.external_url} target="_blank" rel="noreferrer" className="hover:underline">
              {r.title}
            </a>
          </h3>
          {r.location_text && (
            <div className="text-xs text-slate-500 mt-1">{r.location_text}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-bold text-slate-900 tabular-nums">
            {r.is_free ? 'Free' : r.latest_price != null ? `$${Number(r.latest_price).toFixed(0)}` : '—'}
          </div>
          {r.lowest_price != null && r.highest_price != null && r.lowest_price !== r.highest_price && (
            <div className="text-[10px] text-slate-500 mt-0.5">
              range ${Number(r.lowest_price).toFixed(0)}–${Number(r.highest_price).toFixed(0)}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-1.5">
        <ScoreBar label="Match"        value={r.match_score} />
        <ScoreBar label="Trust"        value={r.trust_score} />
        <ScoreBar label="Verification" value={r.verification_coverage} />
      </div>

      <div className="flex items-center gap-1.5 flex-wrap pt-2 border-t border-slate-100">
        <button
          onClick={() => onExplain(r.id)}
          className="text-[11px] px-2.5 py-1 rounded-md border border-slate-200 hover:border-orange-400 hover:bg-orange-50 text-slate-700 transition-colors inline-flex items-center gap-1"
          data-testid={`why-${r.id}`}
          title="Why am I seeing this?"
        >
          <HelpCircle size={12} /> Why?
        </button>
        {DECISIONS.map((d) => (
          <button
            key={d.key}
            onClick={() => onDecide(r.id, d.key)}
            className="text-[11px] px-2.5 py-1 rounded-md border border-slate-200 hover:border-slate-400 hover:bg-slate-50 text-slate-700 transition-colors"
            data-testid={`decide-${d.key}-${r.id}`}
          >
            {d.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const KpiCard = ({ kpi }) => {
  const pct = Math.round((kpi?.reduction || 0) * 100);
  return (
    <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5 flex items-center gap-4"
         data-testid="kpi-attention-reduction">
      <div className="rounded-xl bg-orange-50 p-3 border border-orange-200">
        <ShieldCheck className="text-orange-600" size={22} />
      </div>
      <div className="flex-1">
        <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Attention Reduction · last 30 days</div>
        <div className="flex items-baseline gap-3 mt-1">
          <div className="text-3xl font-bold text-slate-900 tabular-nums">{pct}%</div>
          <div className="text-xs text-slate-600">
            {kpi?.surfaced ?? 0} surfaced · {kpi?.reviewed ?? 0} review · {kpi?.hidden ?? 0} hidden of {kpi?.total ?? 0}
          </div>
        </div>
      </div>
    </div>
  );
};

const ResultsFeed = () => {
  const [items, setItems] = useState([]);
  const [bucket, setBucket] = useState('surface');
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whyResult, setWhyResult] = useState(null);
  const [ruleSuggestions, setRuleSuggestions] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [feed, k, sugg] = await Promise.all([
        api.get('/results', { params: { bucket, limit: 100 } }),
        api.get('/results/kpi/attention-reduction'),
        api.get('/results/suggestions/rules').catch(() => ({ data: { suggestions: [] } })),
      ]);
      setItems(feed.data.results || []);
      setKpi(k.data);
      setRuleSuggestions(sugg.data.suggestions || []);
    } catch (e) {
      toast.error('Could not load persistent results');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [bucket]);

  const decide = async (resultId, decision) => {
    try {
      const r = await api.post('/results/decision', { result_id: resultId, decision });
      toast.success(r.data.message || 'Decision saved');
      load();
    } catch (e) {
      toast.error('Failed to save decision');
    }
  };

  const explain = async (resultId) => {
    try {
      const r = await api.get(`/results/${resultId}/why`);
      setWhyResult(r.data);
    } catch (e) {
      toast.error('Could not load explanation');
    }
  };

  const applySuggestion = async (s) => {
    try {
      await api.post('/rules', {
        label: s.label,
        predicate: s.predicate,
        action: s.action,
        reason: s.reason,
      });
      toast.success('Rule added');
      load();
    } catch (e) {
      toast.error('Failed to add rule');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6" data-testid="results-feed-page">
      <header className="flex items-center gap-3">
        <div className="rounded-xl bg-orange-100 p-2 border border-orange-200">
          <Sparkles className="text-orange-600" size={22} />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Persistent Results</h1>
          <p className="text-sm text-slate-600">
            Zita remembers every result across scans. Decide once — she keeps track.
          </p>
        </div>
      </header>

      {kpi && <KpiCard kpi={kpi} />}

      {ruleSuggestions.length > 0 && (
        <div className="rounded-2xl border-2 border-orange-200 bg-orange-50/50 p-4"
             data-testid="rule-suggestions">
          <div className="text-xs uppercase tracking-wide text-orange-800 font-semibold mb-2">
            Ari noticed a pattern
          </div>
          <div className="space-y-2">
            {ruleSuggestions.slice(0, 2).map((s, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl border border-orange-100 p-3">
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-900">{s.label}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{s.reason}</div>
                </div>
                <button
                  onClick={() => applySuggestion(s)}
                  className="text-xs px-3 py-1.5 rounded-md bg-orange-600 text-white font-semibold hover:bg-orange-700"
                  data-testid={`apply-suggestion-${i}`}
                >
                  Add rule
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {whyResult && (
        <div className="rounded-2xl border-2 border-slate-300 bg-white p-4 space-y-2"
             data-testid="why-drawer">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900">Why am I seeing this?</h3>
            <button onClick={() => setWhyResult(null)} className="text-slate-500 hover:text-slate-900"
                    data-testid="why-close">×</button>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Surfaced reasons</div>
            <ul className="list-disc list-inside text-sm text-slate-800 space-y-1 mt-1">
              {(whyResult.surfaced_reasons || []).map((s, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: s.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>') }} />
              ))}
              {(whyResult.surfaced_reasons || []).length === 0 && <li className="text-slate-500">—</li>}
            </ul>
          </div>
          {(whyResult.hidden_reasons || []).length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold">Reasons it might be hidden</div>
              <ul className="list-disc list-inside text-sm text-slate-800 space-y-1 mt-1">
                {whyResult.hidden_reasons.map((s, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: s.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>') }} />
                ))}
              </ul>
            </div>
          )}
          {(whyResult.evidence || []).length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-600 font-semibold">Evidence trail</div>
              <ul className="text-xs text-slate-700 mt-1 space-y-1">
                {whyResult.evidence.map((e, i) => (
                  <li key={i}>
                    <b>{e.source_key}</b> — authority: {e.authority}
                    {e.stale ? <span className="text-amber-700"> · stale</span> : <span className="text-emerald-700"> · fresh</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2" role="tablist">
        {['surface', 'review', 'hidden'].map((b) => (
          <button
            key={b}
            onClick={() => setBucket(b)}
            data-testid={`bucket-tab-${b}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              bucket === b
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
            }`}
          >
            {b === 'surface' && <Eye className="inline mr-1" size={12} />}
            {b === 'review' && <Filter className="inline mr-1" size={12} />}
            {b === 'hidden' && <EyeOff className="inline mr-1" size={12} />}
            {bucketLabel[b]}
          </button>
        ))}
        <Link
          to="/rules"
          className="ml-auto text-xs text-slate-600 hover:text-slate-900 hover:underline"
          data-testid="link-manage-rules"
        >
          Manage rules →
        </Link>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500" data-testid="results-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center text-slate-500"
             data-testid="results-empty">
          {bucket === 'surface' && 'Nothing surfaced yet — run a scan to populate your feed.'}
          {bucket === 'review' && 'No results are flagged for review right now.'}
          {bucket === 'hidden' && 'You have not hidden anything yet.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="results-list">
          {items.map((r) => <ResultCard key={r.id} r={r} onDecide={decide} onExplain={explain} />)}
        </div>
      )}
    </div>
  );
};

export default ResultsFeed;
